import { useEffect, useRef, useState } from 'react'
import { CreditCard as CreditCardType } from '@shared/firebase/schema'
import toast from 'react-hot-toast'
import { CreditCard, Link2, Save, Trash2 } from 'lucide-react'
import {
  useCardsForReferrals,
  useRemoveReferralLink,
  useUpdateReferralLink,
} from '../lib/queries/referralLinks'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  DataTable,
  useConfirm,
} from '../components/ui'

type InputState = Record<string, { referralUrl: string; notes: string }>

export default function ReferralLinks() {
  const [inputs, setInputs] = useState<InputState>({})

  const { data: cards = [], isLoading } = useCardsForReferrals()
  const updateMutation = useUpdateReferralLink()
  const removeMutation = useRemoveReferralLink()
  const confirm = useConfirm()

  // Hydrate input state from cards that already have a referral link.
  // Only seed each card's inputs ONCE — subsequent refetches must not stomp
  // whatever the user is currently typing. New cards (not previously seen)
  // do get hydrated.
  const hydratedIds = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!cards.length) return
    setInputs(prev => {
      let next = prev
      let changed = false
      for (const card of cards) {
        if (hydratedIds.current.has(card.id)) continue
        hydratedIds.current.add(card.id)
        if (card.mitc?.referralLink) {
          if (!changed) { next = { ...prev }; changed = true }
          next[card.id] = {
            referralUrl: card.mitc.referralLink.url   || '',
            notes:       card.mitc.referralLink.notes || '',
          }
        }
      }
      return next
    })
  }, [cards])

  // Defensive guard preserved from previous version — there was a past
  // bug where corrupted state could land in the URL field.
  const isSuspicious = (v: string) =>
    v.length > 500 || /(User |totalCards|verified)/.test(v)

  const handleInputChange = (cardId: string, field: 'referralUrl' | 'notes', raw: string) => {
    const value = typeof raw === 'string' ? raw : ''
    if (field === 'referralUrl' && isSuspicious(value)) {
      toast.error('Suspicious input detected. Please refresh the page and try again.')
      return
    }
    setInputs(prev => ({
      ...prev,
      [cardId]: { ...(prev[cardId] || { referralUrl: '', notes: '' }), [field]: value },
    }))
  }

  const handleSave = (card: CreditCardType) => {
    const input = inputs[card.id] || { referralUrl: '', notes: '' }
    const referralUrl = input.referralUrl.trim()
    const notes       = (input.notes || '').trim()

    if (!referralUrl) { toast.error('Please enter a referral URL'); return }
    if (isSuspicious(referralUrl)) { toast.error('Corrupted data detected. Please refresh and try again.'); return }

    updateMutation.mutate(
      { cardId: card.id, referralUrl, notes },
      {
        onSuccess: () => toast.success('Referral link updated'),
        onError:   (e: any) => toast.error(`Failed to update: ${e?.message || e}`),
      },
    )
  }

  const handleRemove = async (card: CreditCardType) => {
    const ok = await confirm({
      title: 'Remove this referral link?',
      body: <>The apply button on <strong>{card.name}</strong> will fall back to its MITC source URL (if any).</>,
      confirmLabel: 'Remove link',
      destructive: true,
    })
    if (!ok) return
    removeMutation.mutate(card.id, {
      onSuccess: () => {
        toast.success('Referral link removed')
        setInputs(prev => ({ ...prev, [card.id]: { referralUrl: '', notes: '' } }))
      },
      onError: () => toast.error('Failed to remove referral link'),
    })
  }

  // Coverage stats
  const withMitc     = cards.filter(c => !!c.mitc).length
  const withReferral = cards.filter(c => !!c.mitc?.referralLink?.url).length
  const coverage     = cards.length ? (withReferral / cards.length) * 100 : 0

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader title="Referral Links" subtitle="Loading…" />
        <Card unpadded>
          <div className="p-6 space-y-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Referral Links"
        subtitle="Manage admin referral links per card — these take priority over MITC source URLs in the user app."
      />

      {/* Coverage stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <p className="text-xs uppercase tracking-wider font-medium text-ink-muted">Total cards</p>
          <p className="mt-1 font-display text-3xl text-ink tabular-nums">{cards.length}</p>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wider font-medium text-ink-muted">With MITC</p>
          <p className="mt-1 font-display text-3xl text-brand tabular-nums">{withMitc}</p>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wider font-medium text-ink-muted">With referral link</p>
          <p className="mt-1 font-display text-3xl text-success tabular-nums">{withReferral}</p>
        </Card>
        <Card accent>
          <p className="text-xs uppercase tracking-wider font-medium text-ink-muted">Coverage</p>
          <p className="mt-1 font-display text-3xl text-accent-deep dark:text-accent-bright tabular-nums">
            {coverage.toFixed(1)}<span className="text-xl text-ink-muted">%</span>
          </p>
        </Card>
      </div>

      {/* Referral links table */}
      {cards.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Link2 size={24} />}
            title="No credit cards"
            description="Once cards are added (manually or via MITC import) they'll appear here for referral-link management."
          />
        </Card>
      ) : (
        <DataTable>
          <THead>
            <TR>
              <TH>Card</TH>
              <TH>Referral URL</TH>
              <TH>Notes</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {cards.map(card => {
              const input       = inputs[card.id] || { referralUrl: '', notes: '' }
              const hasReferral = !!card.mitc?.referralLink?.url
              const busy        = updateMutation.isPending || removeMutation.isPending

              return (
                <TR key={card.id}>
                  <TD>
                    <div>
                      <p className="text-sm font-medium text-ink">{card.name}</p>
                      <p className="text-xs text-ink-subtle">{card.bank}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        {hasReferral && <Badge tone="success">Referral set</Badge>}
                        {card.mitc
                          ? <Badge tone="brand">MITC</Badge>
                          : <Badge tone="neutral">Manual</Badge>}
                        {card.createdBy === 'manual' && <Badge tone="accent">Admin</Badge>}
                      </div>
                      {card.mitc?.sourceUrl && (
                        <p className="mt-1 text-[11px] text-ink-subtle">
                          Fallback: MITC source URL available
                        </p>
                      )}
                    </div>
                  </TD>
                  <TD className="min-w-[260px]">
                    <Input
                      type="url"
                      placeholder="https://example.com/referral-link"
                      value={input.referralUrl}
                      onChange={e => handleInputChange(card.id, 'referralUrl', e.target.value)}
                      disabled={busy}
                    />
                  </TD>
                  <TD className="min-w-[180px]">
                    <Input
                      type="text"
                      placeholder="Optional notes…"
                      value={input.notes}
                      onChange={e => handleInputChange(card.id, 'notes', e.target.value)}
                      disabled={busy}
                    />
                  </TD>
                  <TD>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<Save size={14} />}
                        loading={updateMutation.isPending}
                        disabled={!input.referralUrl.trim() || busy}
                        onClick={() => handleSave(card)}
                      >
                        {hasReferral ? 'Update' : 'Save'}
                      </Button>
                      {hasReferral && (
                        <Button
                          variant="destructive"
                          size="sm"
                          leftIcon={<Trash2 size={14} />}
                          loading={removeMutation.isPending}
                          disabled={busy}
                          onClick={() => handleRemove(card)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              )
            })}
          </TBody>
        </DataTable>
      )}

      {/* Inline help */}
      <Card>
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-info/10 text-info flex items-center justify-center flex-shrink-0">
            <CreditCard size={16} />
          </div>
          <div className="text-sm text-ink-muted">
            Admin referral links override any MITC source URL on the user's apply button.
            Removing a link falls back to the MITC source URL when present, otherwise the
            apply button is hidden.
          </div>
        </div>
      </Card>
    </div>
  )
}
